import { repositoryFactory, DataSourceStrategy, CacheStrategy } from '@rammetec/data-access';
import { rtLogService } from '@rammetec/core';
import { USER_DATA } from '../types/user.js';
import { USER_ORGANIZATION } from '../types/organization.js';
import { lifecycleService } from './lifecycleService.js';
import { auditService } from './auditService.js';
class UserService {
    getRepository() {
        return repositoryFactory.getRepository('user', USER_DATA, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
    }
    /**
     * Search users by email with client-side filtering.
     * Returns up to 50 results matching the search term.
     */
    async searchByEmail(searchTerm) {
        try {
            const repository = await this.getRepository();
            if (!searchTerm) {
                // If no search term, return all users sorted by email (limited to 50)
                const users = await repository.getAll({
                    sort: { field: 'email', direction: 'asc' }
                });
                return users.slice(0, 50);
            }
            // Fetch all users and filter client-side
            // For better performance, we could use pagination or server-side filtering
            // But for now, this is simpler and works well for small-medium datasets
            const allUsers = await repository.getAll({
                sort: { field: 'email', direction: 'asc' }
            });
            // Filter by email containing the search term (case-insensitive)
            const searchLower = searchTerm.toLowerCase();
            const filtered = allUsers.filter(user => user.email?.toLowerCase().includes(searchLower));
            // Return up to 50 results
            return filtered.slice(0, 50);
        }
        catch (error) {
            rtLogService.error('Error searching users by email', { error });
            return [];
        }
    }
    /**
     * Get user by ID
     */
    async getUserById(userId) {
        try {
            const repository = await this.getRepository();
            return await repository.getById(userId);
        }
        catch (error) {
            rtLogService.error('Error fetching user by ID', { error, userId });
            return null;
        }
    }
    /**
     * Get all users
     */
    async getAllUsers() {
        try {
            const repository = await this.getRepository();
            return await repository.getAll({
                sort: { field: 'name', direction: 'asc' }
            });
        }
        catch (error) {
            rtLogService.error('Error fetching all users', { error });
            return [];
        }
    }
    /**
     * Get organization IDs for a user.
     * Returns array of organization IDs from user/{userId}/orgs collection.
     */
    async getUserOrganizationIds(userId) {
        try {
            const userOrgRepository = await repositoryFactory.getRepository(`user/${userId}/orgs`, USER_ORGANIZATION, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            const orgs = await userOrgRepository.getAll();
            return orgs.map(org => org.id);
        }
        catch (error) {
            rtLogService.error('Error fetching user organization IDs', { error, userId });
            return [];
        }
    }
    /**
     * Read the user–org link at user/{userId}/orgs/{orgId}.
     * Returns null when the link does not exist.
     */
    async getUserOrganization(userId, orgId) {
        try {
            const userOrgRepository = await repositoryFactory.getRepository(`user/${userId}/orgs`, USER_ORGANIZATION, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            return await userOrgRepository.getById(orgId);
        }
        catch (error) {
            rtLogService.error('Error fetching user organization link', { error, userId, orgId });
            return null;
        }
    }
    /**
     * Set aiAssistantEnabled on user/{userId}/orgs/{orgId}.
     * Absent on the link means enabled; callers write an explicit boolean.
     */
    async setUserAiAssistantEnabled(userId, orgId, aiAssistantEnabled) {
        try {
            const userOrgRepository = await repositoryFactory.getRepository(`user/${userId}/orgs`, USER_ORGANIZATION, DataSourceStrategy.Firestore, CacheStrategy.InMemory);
            try {
                await userOrgRepository.getById(orgId);
                await userOrgRepository.update(orgId, { aiAssistantEnabled });
            }
            catch {
                // Link missing (unusual on update) — create the minimal reverse link.
                await userOrgRepository.create({
                    id: orgId,
                    userId,
                    orgId,
                    role: 'user',
                    aiAssistantEnabled,
                });
            }
            rtLogService.info('User AI assistant flag updated', { userId, orgId, aiAssistantEnabled });
        }
        catch (error) {
            rtLogService.error('Error updating user AI assistant flag', {
                error,
                userId,
                orgId,
                aiAssistantEnabled,
            });
            throw error;
        }
    }
    /**
     * Set the current organization for a user.
     * Updates the currentOrg field in the user document.
     */
    async setUserCurrentOrganization(userId, orgId) {
        try {
            const repository = await this.getRepository();
            await repository.update(userId, {
                currentOrg: orgId
            });
        }
        catch (error) {
            rtLogService.error('Error setting user current organization', { error, userId, orgId });
            throw error;
        }
    }
    /**
     * Get user data by ID.
     * Alias for getUserById for compatibility with organizationService.
     */
    async getUserData(userId) {
        return this.getUserById(userId);
    }
    /**
     * Creates (or overwrites) the UserData document in Firestore.
     * ID must be provided (matches the Firebase Auth UID).
     */
    async createUserData(data) {
        try {
            const repository = await this.getRepository();
            // Derive the denormalized `active` flag from the cutoff and stamp audit
            // dates — both owned by the domain so they can't be forgotten / reused by RN.
            const stamped = auditService.stampOnCreate(lifecycleService.stampActive(data));
            return await repository.create(stamped);
        }
        catch (error) {
            rtLogService.error('Error creating user data', { error, userId: data.id });
            throw error;
        }
    }
    /**
     * Updates fields on an existing UserData document.
     */
    async updateUserData(userId, data) {
        try {
            const repository = await this.getRepository();
            // Keep the denormalized `active` flag in sync whenever the cutoff changes.
            const withActive = 'inactivationDate' in data
                ? { ...data, active: lifecycleService.isActive({ inactivationDate: data.inactivationDate }) }
                : { ...data };
            // Refresh last-modified metadata on every update.
            const patch = auditService.stampOnUpdate(withActive);
            await repository.update(userId, patch);
            rtLogService.info('User data updated', { userId });
        }
        catch (error) {
            rtLogService.error('Error updating user data', { error, userId });
            throw error;
        }
    }
}
export const userService = new UserService();
//# sourceMappingURL=userService.js.map